import type { ButtonHTMLAttributes, ReactNode } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'lg' | 'xl';
  children: ReactNode;
}

// Borders are written as longhand (width, style, color) everywhere. Mixing the `border` shorthand
// with `borderColor` makes React warn when a button changes variant, like the Reports tabs.
const base: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  minHeight: 'var(--touch)',
  padding: '0 20px',
  borderRadius: 'var(--radius-sm)',
  fontWeight: 600,
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: 'transparent',
  cursor: 'pointer',
  textDecoration: 'none',
};
const variants: Record<NonNullable<ButtonProps['variant']>, React.CSSProperties> = {
  primary: { background: 'var(--crave-red)', color: '#fff' },
  secondary: { background: 'var(--paper)', color: 'var(--ink)', borderColor: 'var(--line)' },
  ghost: { background: 'transparent', color: 'var(--ink)' },
  danger: {
    background: 'var(--paper)',
    color: 'var(--crave-red)',
    borderColor: 'var(--crave-red)',
  },
};
const sizes: Record<NonNullable<ButtonProps['size']>, React.CSSProperties> = {
  md: {},
  lg: { minHeight: 56, fontSize: 18, padding: '0 28px' },
  xl: {
    minHeight: 88,
    fontSize: 28,
    padding: '0 36px',
    width: '100%',
    borderRadius: 'var(--radius)',
  },
};

export function Button({
  variant = 'primary',
  size = 'md',
  style,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled}
      style={{
        ...base,
        ...variants[variant],
        ...sizes[size],
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
}
